import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { ToolbarAccountComponent } from '@layout/toolbar/toolbar-account.component';
import { CurrentUserService } from '@shared/services';
import { Popover, PopoverModule } from 'primeng/popover';
import { ToolbarModule } from 'primeng/toolbar';
import { appNavigation } from './header.helper';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: true,
    imports: [CommonModule, ToolbarModule, RouterLink, RouterLinkActive, PopoverModule, ToolbarAccountComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnDestroy {
    private currentUserService = inject(CurrentUserService);
    private scrollListener: (() => void) | undefined;

    ovAccounts = viewChild<Popover>('ovAccounts');
    ovAssays = viewChild<Popover>('ovAssays');
    ovOrders = viewChild<Popover>('ovOrders');
    ovPricing = viewChild<Popover>('ovPricing');
    ovProbes = viewChild<Popover>('ovProbes');
    ovPm = viewChild<Popover>('ovPm');
    ovSales = viewChild<Popover>('ovSales');
    ovShipping = viewChild<Popover>('ovShipping');
    ovStrains = viewChild<Popover>('ovStrains');
    ovTagCenter = viewChild<Popover>('ovTagCenter');
    ovCheckmate = viewChild<Popover>('ovCheckmate');
    ovSequencing = viewChild<Popover>('ovSequencing');

    readonly appNavigation = appNavigation;
    currentUserFirstName = computed(() => this.currentUserService.currentUser().firstName);
    currentUserInitialized = computed(() => !!this.currentUserService.currentUser().id);

    overlayStates = signal<Record<string, boolean>>({
        accounts: false,
        assays: false,
        orders: false,
        pricing: false,
        probes: false,
        pm: false,
        sales: false,
        shipping: false,
        strains: false,
        tagCenter: false,
        checkmate: false,
        sequencing: false,
    });

    ngOnDestroy() {
        this.removeScrollListener();
    }

    navItemHover(id: string, event: MouseEvent) {
        Object.entries(this.overlayStates()).forEach(([key, value]) => {
            if (value) {
                const otherPopover = this.getPopoverForId(key);
                otherPopover?.hide();
            }
        });

        const popover = this.getPopoverForId(id);
        const newState = Object.fromEntries(Object.keys(this.overlayStates()).map(key => [key, false]));

        if (popover) {
            newState[id] = true;
            popover.show(event);
            this.addScrollListener();
        }

        this.overlayStates.set(newState);
    }

    hideAllOverlays() {
        this.ovAccounts()?.hide();
        this.ovAssays()?.hide();
        this.ovOrders()?.hide();
        this.ovPricing()?.hide();
        this.ovProbes()?.hide();
        this.ovPm()?.hide();
        this.ovSales()?.hide();
        this.ovShipping()?.hide();
        this.ovStrains()?.hide();
        this.ovTagCenter()?.hide();
        this.ovCheckmate()?.hide();
        this.ovSequencing()?.hide();

        this.overlayStates.set({
            accounts: false,
            assays: false,
            orders: false,
            pricing: false,
            probes: false,
            pm: false,
            sales: false,
            shipping: false,
            strains: false,
            tagCenter: false,
            checkmate: false,
            sequencing: false,
        });

        this.removeScrollListener();
    }

    private getPopoverForId(id: string): Popover | undefined {
        switch (id) {
            case 'accounts':
                return this.ovAccounts();
            case 'assays':
                return this.ovAssays();
            case 'orders':
                return this.ovOrders();
            case 'pricing':
                return this.ovPricing();
            case 'probes':
                return this.ovProbes();
            case 'pm':
                return this.ovPm();
            case 'sales':
                return this.ovSales();
            case 'shipping':
                return this.ovShipping();
            case 'strains':
                return this.ovStrains();
            case 'tag-center':
                return this.ovTagCenter();
            case 'ckm':
                return this.ovCheckmate();
            case 'seq':
                return this.ovSequencing();
            default:
                return undefined;
        }
    }

    private addScrollListener() {
        if (this.scrollListener) {
            return;
        }

        this.scrollListener = () => {
            this.hideAllOverlays();
        };

        window.addEventListener('scroll', this.scrollListener, { passive: true });
    }

    private removeScrollListener() {
        if (this.scrollListener) {
            window.removeEventListener('scroll', this.scrollListener);
            this.scrollListener = undefined;
        }
    }
}
